import { projectsService } from '@/services/projectsService/projectsService';
import {
  AllProjectsResponseDto,
  CreateProjectDto,
  CreateProjectResponseDto,
  GetProjectByIdResponseDto,
  GetProjectsDropDownDto,
  Project,
  ProjectDropdownDto,
  ProjectFilters,
  ProjectStats,
  UpdateProjectDto,
} from '@/types/project.types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { RootState } from '../..';

interface ProjectsState {
  list: {
    data: Project[];
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
    projectsDropdown?: ProjectDropdownDto[];
  };

  selected: Project | null;
  selectedProjectStats: ProjectStats | null;
  loading: boolean;
  updatingProject: boolean;
  error: string | null;
  ProjectsFilterOptions: any[];
}

const initialState: ProjectsState = {
  list: {
    data: [],
    projectsDropdown: [],
  },
  selected: null,
  selectedProjectStats: null,
  loading: false,
  updatingProject: false,
  error: null,
  ProjectsFilterOptions: [],
};

export const fetchProjectsList = createAsyncThunk<
  AllProjectsResponseDto,
  ProjectFilters | undefined,
  { rejectValue: string }
>('projects/fetchAll', async (filters, { rejectWithValue }) => {
  try {
    return await projectsService.getProjectsList(filters);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Error fetching projects';
    return rejectWithValue(message);
  }
});

export const createProjects = createAsyncThunk<
  CreateProjectResponseDto,
  CreateProjectDto,
  {
    rejectValue: string;
  }
>('projects/create', async (data, { rejectWithValue }) => {
  try {
    return await projectsService.createProjects(data);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error creating project';

    return rejectWithValue(apiMessage);
  }
});

export const getProjectById = createAsyncThunk<
  GetProjectByIdResponseDto,
  string,
  {
    rejectValue: string;
  }
>('projects/getById', async (id: string, { rejectWithValue }) => {
  try {
    return await projectsService.getProjectsById(id);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Error fetching projectById';
    return rejectWithValue(message);
  }
});

export const updateProjects = createAsyncThunk<
  Project,
  { id: string; data: UpdateProjectDto },
  {
    rejectValue: string;
  }
>('projects/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await projectsService.updateProjects(id, data);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error updating project';

    return rejectWithValue(apiMessage);
  }
});

export const deleteProjects = createAsyncThunk(
  'projects/softDelete',
  async ({ id, deletedBy }: { id: string; deletedBy: string }, thunkAPI) => {
    try {
      return await projectsService.deleteProjects(id, deletedBy);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || 'Error deleting Projects');
    }
  },
);

export const getProjectsDropdown = createAsyncThunk<
  GetProjectsDropDownDto,
  void,
  { state: RootState; rejectValue: string }
>(
  'projects/dropdown',
  async (_, { rejectWithValue }) => {
    try {
      return await projectsService.getProjectsDropdown();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Error fetching projectById';
      return rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { list } = getState().projects;
      return list.projectsDropdown?.length === 0;
    },
  },
);

export const exportProjectConsentUsers = createAsyncThunk<Blob, number, { rejectValue: string }>(
  'projects/exportConsentUsers',
  async (projectId, { rejectWithValue }) => {
    try {
      return await projectsService.exportProjectConsentUsers(projectId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Error exporting consent users';
      return rejectWithValue(message);
    }
  },
);

export const addUserToProject = createAsyncThunk(
  'projects/add-user',
  async (
    {
      projectId,
      phoneNumber,
      blueNumberId,
      userId,
    }: { projectId: number; phoneNumber: string; blueNumberId: string; userId: number },
    thunkAPI,
  ) => {
    try {
      return await projectsService.addUserToProject(projectId, phoneNumber, blueNumberId, userId);
    } catch (error) {
      const err = error as AxiosError<any>;
      const message = err.response?.data?.message;
      return thunkAPI.rejectWithValue(message || 'Error adding user to project');
    }
  },
);

export const verifyMobileUser = createAsyncThunk(
  'projects/verify-mobile',
  async (
    {
      phoneNumber,
      countryCode,
      blueNumberId,
    }: { phoneNumber: string; countryCode: string; blueNumberId: string },
    thunkAPI,
  ) => {
    try {
      return await projectsService.verifyMobileUser(phoneNumber, countryCode, blueNumberId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'User not found';
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    clearProjectsError(state) {
      state.error = null;
    },
    clearSelectedProject(state) {
      state.selected = null;
      state.selectedProjectStats = null;
    },
    clearProjectsDropdown(state) {
      state.list.projectsDropdown = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectsList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProjectsList.fulfilled, (state, action) => {
        state.loading = false;

        const { data, meta } = action.payload;

        if (meta.page === 1) {
          state.list.data = data;
        } else {
          const existingIds = new Set(state.list.data.map((p) => p.id));
          const newProjects = data.filter((p) => !existingIds.has(p.id));
          state.list.data = [...state.list.data, ...newProjects];
        }

        state.list.total = meta.total;
        state.list.page = meta.page;
        state.list.limit = meta.limit;
        state.list.totalPages = meta.totalPages;
      })

      .addCase(fetchProjectsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getProjectById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProjectById.fulfilled, (state, action) => {
        state.loading = false;
        state.selected = action.payload.data;
        state.selectedProjectStats = action.payload.count!;
      })
      .addCase(getProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.list.data.unshift(action.payload.data);
        if (state.list.total) state.list.total += 1;
      })
      .addCase(createProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateProjects.pending, (state) => {
        state.updatingProject = true;
        state.error = null;
      })
      .addCase(updateProjects.fulfilled, (state, action) => {
        state.updatingProject = false;
      })
      .addCase(updateProjects.rejected, (state, action) => {
        state.updatingProject = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProjects.fulfilled, (state, action) => {
        state.loading = false;
        // Remove from list if needed
        // state.list.data = state.list.data.filter(p => p.id !== action.meta.arg);
      })
      .addCase(deleteProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getProjectsDropdown.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectsDropdown.fulfilled, (state, action) => {
        state.loading = false;
        state.list.projectsDropdown = action.payload.data;
      })
      .addCase(getProjectsDropdown.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.list.projectsDropdown = [];
      });
  },
});

export const { clearProjectsError, clearSelectedProject, clearProjectsDropdown } =
  projectsSlice.actions;
export default projectsSlice.reducer;
